<template>
  <div data-test="costs">
    <h2>{{ t('costs.TITLE_COSTS') }}</h2>
    <dl>
      <dt v-if="hasRentNet">{{ t('costs.RENT_NET') }}:</dt>
      <dd v-if="hasRentNet">
        <ListingPrice
          :currency="currency"
          :offerType="listing.offerType as OfferType"
          :priceType="HgPriceType.NET"
          :listingPriceInfo="listingPriceInfo"
        />
      </dd>
      <dt v-if="hasRentExtra">{{ t('costs.RENT_EXTRA') }}:</dt>
      <dd v-if="hasRentExtra">
        <ListingPrice
          :currency="currency"
          :offerType="listing.offerType as OfferType"
          :priceType="HgPriceType.EXTRA"
          :listingPriceInfo="listingPriceInfo"
        />
      </dd>
      <dt>{{ t(costsMainPriceLabel) }}:</dt>
      <dd>
        <strong
          ><ListingPrice
            :currency="currency"
            :offerType="listing.offerType as OfferType"
            :listingPriceInfo="listingPriceInfo"
        /></strong>
        <AdditionalCostTooltip v-if="shouldShowNettoOnlyTooltip" />
      </dd>
      <dd v-if="showPriceDropNotification">
        <PriceDropNotification
          :listing="listing"
          @savePriceDropAlert="emit('savePriceDropAlert', $event)"
        />
      </dd>
    </dl>
  </div>
</template>

<script lang="ts" setup>
import { Buy, Listing, OfferType, Rent } from '@smg-real-estate/hg-rets-schema';
import { computed } from 'vue';

import AdditionalCostTooltip from '@/components/AdditionalCostTooltip/AdditionalCostTooltip.vue';
import ListingPrice from '@/components/ListingPrice/ListingPrice.vue';
import PriceDropNotification from '@/components/PriceDropNotification/PriceDropNotification.vue';
import { getListingCurrency, getListingPriceData } from '@/helpers/ListingData/ListingData';
import { HgPriceType } from '@/models/HgPriceType';
import { isPriceDropAlertSupportedBasePackage } from '@/store/types/price-drop-alert-state';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const COSTS_MAIN_PRICE_LABEL = {
  [OfferType.Rent]: 'costs.RENT_GROSS',
  [OfferType.Buy]: 'costs.BUY_PRICE',
};

defineOptions({
  name: 'Costs',
});
interface Props {
  listing: Listing;
  listerBranding: ListerBranding | null;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  savePriceDropAlert: [event: unknown];
}>();

const showPriceDropNotification = computed((): boolean => {
  return (
    props.listing.offerType === OfferType.Buy &&
    isPriceDropAlertSupportedBasePackage(props.listerBranding?.basePackage) &&
    !!props.listing.prices?.buy?.price
  );
});
const currency = computed((): string | undefined => {
  return getListingCurrency(props.listing);
});
const listingPriceInfo = computed((): Buy | Rent | undefined => {
  return getListingPriceData(props.listing);
});
const costsMainPriceLabel = computed((): string => {
  return props.listing.offerType ? COSTS_MAIN_PRICE_LABEL[props.listing.offerType] : '';
});
const hasRentNet = computed((): boolean => {
  return !!props.listing.prices?.rent?.net;
});
const hasRentExtra = computed((): boolean => {
  return !!props.listing.prices?.rent?.extra;
});
const shouldShowNettoOnlyTooltip = computed((): boolean => {
  if (props.listing.offerType !== OfferType.Rent) {
    return false;
  }

  const rentData = props.listing.prices?.rent;
  if (!rentData?.net) {
    return false;
  }

  return !rentData?.gross || rentData?.gross === rentData?.net;
});
</script>
